// Game host on the app side: sandboxed iframe (opaque origin) + worker, typed messages, watchdog.
// The host trusts nothing that comes up: every message is checked before being forwarded.

export interface GameResult { score: number; completed: boolean; summary: string }
export interface TrialReport { seconds: number; frames: number; ended: boolean; result: GameResult | null }
export type GameEvent =
  | { type: 'ready' } | { type: 'beat'; t: number } | { type: 'progress'; t: number }
  | { type: 'effort'; req: { grade: number; wind: number; crr?: number } }
  | { type: 'toast'; text: string; kind: 'good' | 'bad' | 'info' } | { type: 'sound'; name: string } | { type: 'score'; value: number }
  | { type: 'end'; result: GameResult } | { type: 'result'; result: GameResult | null } | { type: 'report'; report: TrialReport }
  | { type: 'error'; message: string; stack?: string } | { type: 'snapshot'; bitmap: ImageBitmap };
export interface GameInit { seed: number; player: { name: string; ftp: number; mass: number; color: string }; workout: boolean; mode: 'play' | 'test' | 'demo' | 'shot'; test?: { seconds: number; ftp: number; workout: boolean };
  /** Canvas size for a thumbnail shot, used by the frame while it is not laid out yet. */
  size?: { width: number; height: number } }

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const str = (v: unknown, max: number) => String(v ?? '').slice(0, max);
const result = (r: any): GameResult | null => (r && typeof r === 'object' ? { score: num(r.score), completed: !!r.completed, summary: str(r.summary, 200) } : null);

/** Worker message reduced to a safe shape, or null if unknown. */
export function sanitize(m: any): GameEvent | null {
  switch (m?.type) {
    case 'ready': return { type: 'ready' };
    case 'beat': case 'progress': return { type: m.type, t: num(m.t) };
    case 'effort': return { type: 'effort', req: { grade: num(m.req?.grade), wind: num(m.req?.wind), ...(m.req?.crr != null ? { crr: num(m.req.crr) } : {}) } };
    case 'toast': return { type: 'toast', text: str(m.text, 120), kind: m.kind === 'good' || m.kind === 'bad' ? m.kind : 'info' };
    case 'sound': return { type: 'sound', name: str(m.name, 20) };
    case 'score': return { type: 'score', value: num(m.value) };
    case 'end': return { type: 'end', result: result(m.result) ?? { score: 0, completed: false, summary: '' } };
    case 'result': return { type: 'result', result: result(m.result) };
    case 'report': return { type: 'report', report: { seconds: num(m.report?.seconds), frames: num(m.report?.frames), ended: !!m.report?.ended, result: result(m.report?.result) } };
    case 'error': return { type: 'error', message: str(m.message, 500), stack: str(m.stack, 800) };
    // raw image only (never SVG or URL), of bounded size; the host redraws it before displaying it
    case 'snapshot': return typeof ImageBitmap !== 'undefined' && m.bitmap instanceof ImageBitmap && m.bitmap.width > 0 && m.bitmap.width <= 4096 && m.bitmap.height > 0 && m.bitmap.height <= 4096 ? { type: 'snapshot', bitmap: m.bitmap } : null;
    default: return null;
  }
}

export const sandboxUrl = () => new URL('sandbox.html', document.baseURI).href;

/** A token bucket: `burst` at once, then `perS` per second. */
function bucket(burst: number, perS: number) {
  let tokens = burst, at = performance.now();
  return () => {
    const now = performance.now();
    tokens = Math.min(burst, tokens + ((now - at) / 1000) * perS); at = now;
    if (tokens < 1) return false;
    tokens--;
    return true;
  };
}
/** More messages than this in a second is no game, it is a flood: the game is shut down. */
export const MAX_MESSAGES_PER_S = 1000;

export class GameHost {
  readonly frame: HTMLIFrameElement;
  private ready: Promise<void>;
  private onMessage: (e: MessageEvent) => void;
  private watchdog = 0;
  private lastBeat = 0;
  private resultWaiter: ((r: GameResult | null) => void) | null = null;
  private dead = false;
  private paused = false;
  /** What a game shows or plays is bounded here, not in the SDK: the game's code shares the worker with the SDK. */
  private limits: Partial<Record<GameEvent['type'], () => boolean>> = { toast: bucket(3, 2), sound: bucket(8, 10) };
  private flood = { n: 0, since: performance.now() };
  /** A hidden tab stops the game's frames: its silence then says nothing about a frozen game. */
  private onVisibility = () => {
    if (this.paused || this.dead) return;
    this.lastBeat = document.hidden ? Infinity : performance.now();
  };

  constructor(container: HTMLElement, private onEvent: (e: GameEvent) => void) {
    const f = (this.frame = document.createElement('iframe'));
    f.setAttribute('sandbox', 'allow-scripts');
    f.setAttribute('referrerpolicy', 'no-referrer');
    f.setAttribute('allow', '');
    f.setAttribute('tabindex', '-1');
    f.title = 'Jeu';
    f.className = 'game-frame';
    let resolve!: () => void;
    this.ready = new Promise(r => (resolve = r));
    this.onMessage = e => {
      if (e.source !== f.contentWindow) return;
      if (e.data?.type === 'frame-ready') return resolve();
      if (this.dead) return;
      const now = performance.now();
      if (now - this.flood.since > 1000) this.flood = { n: 0, since: now };
      if (++this.flood.n > MAX_MESSAGES_PER_S) { this.onEvent({ type: 'error', message: 'The game sent too many messages and was shut down' }); this.kill(); return; }
      const m = sanitize(e.data);
      if (!m || this.limits[m.type]?.() === false) return;
      if (m.type === 'beat') this.lastBeat = performance.now();
      if (m.type === 'result' && this.resultWaiter) { this.resultWaiter(m.result); this.resultWaiter = null; }
      if (m.type === 'error') this.kill();
      this.onEvent(m);
    };
    addEventListener('message', this.onMessage);
    document.addEventListener('visibilitychange', this.onVisibility);
    f.src = sandboxUrl();
    container.appendChild(f);
  }

  async load(code: string, init: GameInit) {
    await this.ready;
    this.frame.contentWindow!.postMessage({ type: 'load', code, init }, '*');
    if (init.mode !== 'test' && init.mode !== 'shot') {
      // a frozen game (infinite loop) stops beating: it is killed after 5 s
      this.lastBeat = document.hidden ? Infinity : performance.now() + 5000;
      this.watchdog = window.setInterval(() => { if (performance.now() - this.lastBeat > 5000) { this.onEvent({ type: 'error', message: 'The game stopped responding and was shut down' }); this.kill(); } }, 1000);
    }
  }
  private post(m: unknown) { if (!this.dead) this.frame.contentWindow?.postMessage(m, '*'); }
  input(m: Record<string, unknown>) { this.post({ type: 'input', input: m }); }
  pause() { this.post({ type: 'pause' }); this.paused = true; this.lastBeat = Infinity; }
  resume() { this.post({ type: 'resume' }); this.paused = false; this.lastBeat = document.hidden ? Infinity : performance.now(); }
  /** Asks the game for its result (result function), with a maximum delay. */
  stop(): Promise<GameResult | null> {
    if (this.dead) return Promise.resolve(null);
    return new Promise(res => { this.resultWaiter = res; this.post({ type: 'stop' }); setTimeout(() => { if (this.resultWaiter === res) { this.resultWaiter = null; res(null); } }, 600); });
  }
  private kill() { clearInterval(this.watchdog); this.dead = true; this.frame.src = 'about:blank'; }
  destroy() { this.kill(); removeEventListener('message', this.onMessage); document.removeEventListener('visibilitychange', this.onVisibility); this.frame.remove(); }
}

/**
 * Tests a game before showing it: accelerated play with simulated inputs, in a hidden sandbox.
 * Returns the report, or the error (exception, infinite loop, nothing happens).
 */
export function trialRun(code: string, { ftp = 250, workout = false, seconds = 900, timeout = 25000 } = {}): Promise<{ ok: boolean; report?: TrialReport; error?: string; efforts: number }> {
  return new Promise(resolve => {
    const box = document.createElement('div');
    box.style.cssText = 'position:fixed;left:-10000px;top:0;width:320px;height:180px;visibility:hidden';
    document.body.appendChild(box);
    let efforts = 0, settled = false, last = performance.now();
    const done = (r: { ok: boolean; report?: TrialReport; error?: string }) => { if (settled) return; settled = true; clearInterval(timer); host.destroy(); box.remove(); resolve({ ...r, efforts }); };
    const host = new GameHost(box, e => {
      last = performance.now();
      if (e.type === 'effort') efforts++;
      else if (e.type === 'error') done({ ok: false, error: e.message + (e.stack ? `\n${e.stack}` : '') });
      else if (e.type === 'report') done({ ok: e.report.frames > 30, report: e.report, ...(e.report.frames > 30 ? {} : { error: 'The game ends immediately' }) });
    });
    const t0 = performance.now();
    const timer = window.setInterval(() => {
      if (performance.now() - last > 6000) done({ ok: false, error: 'The game stopped responding (infinite loop?)' });
      else if (performance.now() - t0 > timeout) done({ ok: false, error: 'The game is too slow to simulate' });
    }, 500);
    host.load(code, { seed: 1, player: { name: 'Essai', ftp, mass: 72, color: '#ffd400' }, workout, mode: 'test', test: { seconds, ftp, workout } }).catch(e => done({ ok: false, error: String(e) }));
  });
}

/**
 * Captures a game for its thumbnail: play simulated for a few seconds in a hidden sandbox, then an image
 * redrawn by the app (the game only supplies pixels). Rejects if the game crashes or does not respond.
 */
export function gameShot(code: string, { width, height, seconds = 12, ftp = 250, timeout = 8000 }: { width: number; height: number; seconds?: number; ftp?: number; timeout?: number }): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const box = document.createElement('div');
    box.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;height:${height}px;visibility:hidden`;
    document.body.appendChild(box);
    let settled = false;
    const done = (fn: () => void) => { if (settled) return; settled = true; clearTimeout(timer); host.destroy(); box.remove(); fn(); };
    const host = new GameHost(box, e => {
      if (e.type === 'error') done(() => reject(new Error(e.message)));
      else if (e.type === 'snapshot') done(() => {
        const c = document.createElement('canvas'), b = e.bitmap, k = Math.max(width / b.width, height / b.height);
        c.width = width; c.height = height;
        c.getContext('2d')!.drawImage(b, (width - b.width * k) / 2, (height - b.height * k) / 2, b.width * k, b.height * k);
        b.close();
        resolve(c);
      });
    });
    const timer = setTimeout(() => done(() => reject(new Error('The game does not respond'))), timeout);
    host.load(code, { seed: 3, player: { name: 'Gruvetto', ftp, mass: 72, color: '#ffd400' }, workout: false, mode: 'shot', size: { width, height }, test: { seconds, ftp, workout: false } }).catch(e => done(() => reject(e)));
  });
}
